/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useState } from 'react';
import { Text, TextInput, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { styles } from '../posShared.styles';
import { modalExperience } from '../../../shared/modals/modalExperience.styles';
import { PaymentMethodChipRow } from './PaymentMethodChipRow';
import { CheckoutSaleCustomerSelector } from './CheckoutSaleCustomerSelector';
import { CheckoutQuickAddCustomerSheet } from './CheckoutQuickAddCustomerSheet';
import {
  PosCheckoutPriceListSelector,
  useCheckoutPriceLists,
} from './PosCheckoutPriceListSelector';
import { resolvePriceListPresentation } from '../../application/posCheckoutPresentation';
import { usePosPriceListStore } from '../../store/usePosPriceListStore';
import { useHasBusinessCapability } from '../../../shell/session/hooks/useEffectiveBusinessCapabilities'
import { isGlobalFeatureOn } from '../../../shell/feature-flags/globalFeatureFlagsService'
import { useGlobalFeatureFlags } from '../../../shell/providers/GlobalFeatureFlagsProvider';
import { NumericTextInput } from '../../../shared/ui/NumericTextInput';
import { InventoryIsoDateField } from '../../../shared/ui/InventoryIsoDateField';
import { webTextInputAutofillStyle } from '../../../shared/theme/webTextInput';
import { usePosLoyaltyStore } from '../../../loyalty/store/usePosLoyaltyStore';
import { pickAttachmentInteractive } from '../../../shared/attachments/infra/pickAttachmentFile';
import { isUnexpectedFailureHandled } from '../../../shared/sentry/presentUnexpectedFailure';
import { hideAppToasts, showError } from '../../../shared/ui/appToast';
import { usePosCheckoutStore } from '../../store/usePosCheckoutStore';
import { usePosStore } from '../../store/usePosStore';
import type { PosCheckoutCustomerOption, PosDraftPayment, PosPaymentMethodId, PosPaymentMethodRow } from '../../domain/posTypes';
import { usePosCheckoutContextOptional } from '../contexts/PosCheckoutContext';
import { formatPosMoney } from '../../application/posMoneyFormat';

type Props = {
  visible: boolean;
  paymentDrafts?: PosDraftPayment[];
  paymentMethods?: PosPaymentMethodRow[];
  cashSessionOpen?: boolean | null;
  customerEligibleCustomers?: PosCheckoutCustomerOption[];
  customersTotal?: number;
  customerPageIndex?: number;
  customerPageSize?: number;
  onLoadMoreCustomers?: (pageIndex: number, loadAll?: boolean) => void;
  onUpdateDraft?: (id: string, patch: Partial<PosDraftPayment>) => void;
  onRemoveDraft?: (id: string) => void;
  onAddSplitPayment?: (methodId?: PosPaymentMethodId) => void;
  showSectionTitle?: boolean;
  /** Mobile sheet: nombre/lista/adjunto van detrás de «Más opciones». */
  collapseSecondaryFields?: boolean;
};

export function PosCheckoutPaymentPanel(props: Props) {
  const ctx = usePosCheckoutContextOptional();
  const {
    visible,
    paymentDrafts = [],
    paymentMethods = [],
    cashSessionOpen = null,
    customerEligibleCustomers = [],
    customersTotal = 0,
    customerPageIndex = 0,
    customerPageSize = 1,
    onLoadMoreCustomers = () => {},
    onUpdateDraft = () => {},
    onRemoveDraft = () => {},
    onAddSplitPayment = () => {},
    showSectionTitle = true,
    collapseSecondaryFields = false,
    vuelto = 0,
    saldoPendiente = 0,
    isSaving = false,
  } = {
    ...(ctx ?? {}),
    ...props,
  };
  const [quickAddVisible, setQuickAddVisible] = useState(false);
  const [secondaryOpen, setSecondaryOpen] = useState(false);
  const showInlineDiff = paymentDrafts.length === 1;
  const { enabled: priceListsEnabled, lists: priceLists } = useCheckoutPriceLists();
  const activePriceListId = usePosPriceListStore((s) => s.activePriceListId);

  // Sale-level customer
  const saleCustomerId = usePosCheckoutStore((s) => s.saleCustomerId);
  const cartHasPackage = usePosStore((s) => s.cart.some((item) => item.itemType === 'package'));
  const setSaleCustomerId = usePosCheckoutStore((s) => s.setSaleCustomerId);
  const pickupName = usePosCheckoutStore((s) => s.pickupName);
  const setPickupName = usePosCheckoutStore((s) => s.setPickupName);
  const hasKitchenCapability = useHasBusinessCapability('kitchen_tickets');
  const setFiscalBuyerDraft = usePosCheckoutStore((s) => s.setFiscalBuyerDraft);
  const saleFiscalMode = usePosCheckoutStore((s) => s.saleFiscalMode);

  // Comprobantes de la venta (evidencia cheque/transferencia) — se suben post-venta.
  const pendingAttachments = usePosCheckoutStore((s) => s.pendingAttachments);
  const addPendingAttachment = usePosCheckoutStore((s) => s.addPendingAttachment);
  const removePendingAttachment = usePosCheckoutStore((s) => s.removePendingAttachment);
  const [attachmentBusy, setAttachmentBusy] = useState(false);

  // Loyalty store — unificado con el selector de cliente
  const loyaltyBalance = usePosLoyaltyStore((s) => s.balance);
  const loyaltyProgram = usePosLoyaltyStore((s) => s.program);
  const loyaltyRedeemPoints = usePosLoyaltyStore((s) => s.redeemPoints);
  const loyaltyRedeemDiscount = usePosLoyaltyStore((s) => s.redeemArsDiscount());
  const setLoyaltyCustomer = usePosLoyaltyStore((s) => s.setCustomer);
  const setLoyaltyRedeemPoints = usePosLoyaltyStore((s) => s.setRedeemPoints);

  useEffect(() => {
    if (!visible) {
      setQuickAddVisible(false);
      setSecondaryOpen(false);
    }
  }, [visible]);

  // "A cuenta" usa el selector de cliente de la venta (arriba): es el mismo cliente.
  // Si hay un draft fiado sin cliente, el selector se auto-expande con hint.
  const fiadoNeedsCustomer = paymentDrafts.some((d) => d.methodId === 'fiado' && !d.customerId);

  // La evidencia aplica a medios con respaldo externo (cheque/transferencia).
  const hasChequeDraft = paymentDrafts.some((d) => d.methodId === 'cheque');
  const hasTransferDraft = paymentDrafts.some((d) => d.methodId === 'transfer');
  const showAttachmentSection = hasChequeDraft || hasTransferDraft;

  // Mobile: nombre/lista/adjunto son secundarios — van colapsados tras «Más opciones».
  const showPriceListSection =
    priceListsEnabled &&
    resolvePriceListPresentation(priceLists.length, !!activePriceListId) !== 'hidden';
  const hasSecondaryFields =
    hasKitchenCapability || showPriceListSection || showAttachmentSection;
  const secondaryFieldsCollapsed = collapseSecondaryFields && hasSecondaryFields;

  const handleAddAttachment = async () => {
    if (attachmentBusy || isSaving) return;
    setAttachmentBusy(true);
    try {
      const picked = await pickAttachmentInteractive('pos');
      if (picked) addPendingAttachment(picked);
    } catch (error) {
      if (!isUnexpectedFailureHandled(error)) {
        showError('Adjuntar comprobante', error instanceof Error ? error.message : String(error));
      }
    } finally {
      setAttachmentBusy(false);
    }
  };
  const { flags } = useGlobalFeatureFlags();
  const hasLoyaltyCap = useHasBusinessCapability('customer_loyalty');
  const loyaltyEnabled = isGlobalFeatureOn(flags, 'loyalty_integrations') && hasLoyaltyCap;

  const handleSelectSaleCustomer = (customerId: string | null) => {
    setSaleCustomerId(customerId);
    if (customerId) {
      // Si el usuario tocó "Cobrar" sin cliente y después lo eligió, el toast
      // de validación ("Cliente a cuenta incompleto", etc.) quedaría visible
      // unos segundos más contra un estado que ya se resolvió → lo limpiamos.
      hideAppToasts();
      const customer = customerEligibleCustomers.find((c) => c.id === customerId);
      if (customer) {
        void setLoyaltyCustomer({ id: customer.id, name: customer.name });
        // Si hay drafts a cuenta, setear el customerId ahí también
        for (const draft of paymentDrafts) {
          if (draft.methodId === 'fiado' && draft.customerId !== customerId) {
            onUpdateDraft(draft.id, { customerId });
          }
        }
        const fiadoOnlyCheckout =
          paymentDrafts.some((d) => d.methodId === 'fiado') &&
          !paymentDrafts.some((d) => d.methodId !== 'fiado' && (Number(d.amount) || 0) > 0);
        // Fiado: el cliente es para la cuenta, no implica factura a ese CUIT — CF salvo que elijas comprador en Fiscal.
        if (saleFiscalMode === 'fiscal' && customer.cuit && !fiadoOnlyCheckout) {
          setFiscalBuyerDraft({
            consumidorFinal: false,
            cuit: customer.cuit,
            razonSocial: customer.businessName || customer.name,
            taxCondition: customer.taxCondition ?? null,
            linkedCustomerId: customer.id,
          });
        }
      }
    } else {
      void setLoyaltyCustomer(null);
      // Limpiar customerId de todos los drafts de customer
      for (const draft of paymentDrafts) {
        if (draft.methodId === 'fiado' && draft.customerId) {
          onUpdateDraft(draft.id, { customerId: undefined });
        }
      }
      // Reset comprador fiscal a Consumidor Final al quitar cliente
      if (saleFiscalMode === 'fiscal') {
        setFiscalBuyerDraft({
          consumidorFinal: true,
          cuit: '',
          razonSocial: 'Consumidor final',
          taxCondition: null,
          linkedCustomerId: null,
        });
      }
    }
  };

  const secondaryFields = (
    <>
      {hasKitchenCapability ? (
        <View style={styles.checkoutPickupNameBlock} testID="pos.checkout.pickup-name">
          <Text style={styles.checkoutReceivedLabel}>Nombre para retirar</Text>
          <TextInput
            value={pickupName}
            onChangeText={setPickupName}
            placeholder="Opcional"
            placeholderTextColor={ticketColors.ink3}
            editable={!isSaving}
            maxLength={80}
            accessibilityLabel="Nombre para retirar"
            style={[styles.checkoutPickupNameInput, webTextInputAutofillStyle()]}
          />
        </View>
      ) : null}

      <PosCheckoutPriceListSelector enabled={priceListsEnabled} lists={priceLists} />

      {showAttachmentSection ? (
        <View style={styles.checkoutAttachBlock} testID="pos.checkout.attachments">
          {/* «Comprobante de pago» — no chocar con el panel fiscal «Comprobante fiscal». */}
          <Text style={styles.checkoutSecTitle}>Comprobante de pago</Text>
          <TouchableOpacity
            style={[modalExperience.entitySelector, styles.checkoutAttachDashed]}
            onPress={() => void handleAddAttachment()}
            disabled={attachmentBusy || isSaving}
            accessibilityRole="button"
            accessibilityLabel="Adjuntar comprobante"
            testID="pos.checkout.add-attachment"
          >
            <Ionicons name="attach" size={18} color={ticketColors.ink2} />
            <Text style={modalExperience.filterSelectValue} numberOfLines={1}>
              {attachmentBusy
                ? 'Leyendo archivo…'
                : pendingAttachments.length === 0
                  ? 'Adjuntar foto o PDF (opcional)'
                  : `${pendingAttachments.length} archivo${pendingAttachments.length === 1 ? '' : 's'} elegido${pendingAttachments.length === 1 ? '' : 's'}`}
            </Text>
          </TouchableOpacity>
          {pendingAttachments.map((file, index) => (
            <View key={`${file.fileName}-${index}`} style={styles.checkoutAttachmentChipRow}>
              <Ionicons
                name={file.mimeType === 'application/pdf' ? 'document-text-outline' : 'image-outline'}
                size={16}
                color={ticketColors.ink2}
              />
              <Text style={styles.checkoutAttachmentChipName} numberOfLines={1}>
                {file.fileName}
              </Text>
              <TouchableOpacity
                onPress={() => removePendingAttachment(index)}
                disabled={isSaving}
                accessibilityRole="button"
                accessibilityLabel={`Quitar ${file.fileName}`}
                testID={`pos.checkout.remove-attachment-${index}`}
              >
                <Ionicons name="close" size={16} color={ticketColors.ink2} />
              </TouchableOpacity>
            </View>
          ))}
          {hasChequeDraft ? (
            <Text style={styles.checkoutAttachmentHint}>
              Podés adjuntar la foto del cheque (frente y dorso).
            </Text>
          ) : (
            <Text style={styles.checkoutAttachmentHint}>
              Podés adjuntar el comprobante de la transferencia.
            </Text>
          )}
        </View>
      ) : null}
    </>
  );

  const handleQuickAddCreated = (customerId: string, customerName: string) => {
    // Agregar el cliente nuevo a la lista del store para que aparezca
    // inmediatamente en el selector (sin esperar reload remoto).
    const { customerOptions, setCustomerOptions, setCustomersTotal } = usePosCheckoutStore.getState();
    const exists = customerOptions.some((c) => c.id === customerId);
    if (!exists) {
      const newCustomer: PosCheckoutCustomerOption = {
        id: customerId,
        name: customerName,
        phone: null,
        balance: 0,
        creditLimit: 0,
        blocked: false,
        priceListId: null,
      };
      setCustomerOptions([newCustomer, ...customerOptions]);
      setCustomersTotal(usePosCheckoutStore.getState().customersTotal + 1);
    }
    // Setear como cliente de la venta
    setSaleCustomerId(customerId);
    // Si hay drafts a cuenta, seleccionarlo ahí también
    for (const draft of paymentDrafts) {
      if (draft.methodId === 'fiado') {
        onUpdateDraft(draft.id, { customerId });
      }
    }
    // Sincronizar loyalty store
    void setLoyaltyCustomer({ id: customerId, name: customerName });
    // Recargar la lista remota en background para sincronizar
    void onLoadMoreCustomers(0, true);
  };

  return (
    <View style={[styles.checkoutSectionBlock, styles.checkoutPaymentSection]}>
      {showSectionTitle ? (
        <Text style={styles.checkoutPaymentSectionTitle}>Medio de pago</Text>
      ) : null}

      {/* Flujo del cajero: medio → monto → confirmar. Cliente/nombre/lista van después. */}
      <View style={styles.checkoutPaymentDraftsStack}>
        {paymentDrafts.map((draft, index) => (
          <View
            key={draft.id}
            style={[
              styles.checkoutPaymentDraftPanel,
              paymentDrafts.length > 1 && styles.checkoutPaymentDraftPanelSplit,
            ]}
          >
            {paymentDrafts.length > 1 ? (
              <Text style={styles.checkoutPaymentDraftLabel}>Pago {index + 1}</Text>
            ) : null}
            <PaymentMethodChipRow
              methods={paymentMethods}
              selectedId={draft.methodId}
              onSelect={(methodId) => {
                onUpdateDraft(draft.id, {
                  methodId,
                  // A cuenta hereda el cliente de la venta si ya está elegido.
                  customerId: methodId === 'fiado' ? (saleCustomerId ?? undefined) : undefined,
                });
              }}
              isMethodLocked={(methodId) => methodId === 'cash' && cashSessionOpen === false}
              lockedHint={
                cashSessionOpen === false ? 'Efectivo deshabilitado: caja cerrada.' : undefined
              }
            />
            <View style={styles.checkoutReceivedBox}>
              <Text style={styles.checkoutReceivedLabel}>
                {draft.methodId === 'cash'
                  ? 'Recibido'
                  : draft.methodId === 'fiado'
                    ? 'A cuenta'
                    : 'Monto'}
              </Text>
              <View style={styles.checkoutReceivedAmountRow}>
                <Text style={styles.checkoutReceivedCurrency} accessibilityElementsHidden>
                  $
                </Text>
                <NumericTextInput
                  kind="decimalMoney"
                  value={draft.amount}
                  onChangeText={(value) => onUpdateDraft(draft.id, { amount: value })}
                  placeholder="0,00"
                  placeholderTextColor={ticketColors.ink3}
                  accessibilityLabel={
                    draft.methodId === 'cash'
                      ? 'Recibido'
                      : draft.methodId === 'fiado'
                        ? 'A cuenta'
                        : 'Monto'
                  }
                  editable={!isSaving}
                  testID="pos.checkout.amount-input"
                  style={[styles.checkoutPaymentAmountInput, webTextInputAutofillStyle(), { flex: 1 }]}
                />
              </View>
              {showInlineDiff && saldoPendiente > 0 ? (
                <View style={styles.checkoutReceivedDiff}>
                  <Text style={[styles.checkoutReceivedDiffLabel, styles.checkoutReceivedDiffWarn]}>
                    Faltan
                  </Text>
                  <Text style={[styles.checkoutReceivedDiffValue, styles.checkoutReceivedDiffWarn]}>
                    {formatPosMoney(saldoPendiente)}
                  </Text>
                </View>
              ) : null}
              {showInlineDiff && saldoPendiente <= 0 && vuelto > 0 ? (
                <View style={styles.checkoutReceivedDiff}>
                  <Text style={[styles.checkoutReceivedDiffLabel, styles.checkoutReceivedDiffOk]}>
                    Vuelto
                  </Text>
                  <Text style={[styles.checkoutReceivedDiffValue, styles.checkoutReceivedDiffOk]}>
                    {formatPosMoney(vuelto)}
                  </Text>
                </View>
              ) : null}
            </View>
            {draft.methodId === 'cheque' ? (
              <View style={styles.checkoutReceivedBox} testID="pos.checkout.check-fields">
                <Text style={styles.checkoutReceivedLabel}>Datos del cheque</Text>
                <View style={styles.checkoutReceivedAmountRow}>
                  <TextInput
                    value={draft.checkNumber ?? ''}
                    onChangeText={(value) => onUpdateDraft(draft.id, { checkNumber: value })}
                    placeholder="Número (obligatorio)"
                    placeholderTextColor={ticketColors.ink3}
                    editable={!isSaving}
                    accessibilityLabel="Número de cheque"
                    testID="pos.checkout.check-number"
                    style={[styles.checkoutCheckInput, webTextInputAutofillStyle(), { flex: 1 }]}
                  />
                  <View style={{ flex: 1 }} testID="pos.checkout.check-due-date">
                    <InventoryIsoDateField
                      value={draft.checkDueDate ?? ''}
                      onChange={(iso) => onUpdateDraft(draft.id, { checkDueDate: iso })}
                      emptyLabel="Vencimiento"
                      ariaLabel="Vencimiento del cheque"
                      clearButtonLabel="Quitar"
                      size="compact"
                    />
                  </View>
                </View>
                <View style={[styles.checkoutReceivedAmountRow, { marginTop: 6 }]}>
                  <TextInput
                    value={draft.checkDrawer ?? ''}
                    onChangeText={(value) => onUpdateDraft(draft.id, { checkDrawer: value })}
                    placeholder="Librador"
                    placeholderTextColor={ticketColors.ink3}
                    editable={!isSaving}
                    accessibilityLabel="Librador del cheque"
                    style={[styles.checkoutCheckInput, webTextInputAutofillStyle(), { flex: 1 }]}
                  />
                  <TextInput
                    value={draft.checkBank ?? ''}
                    onChangeText={(value) => onUpdateDraft(draft.id, { checkBank: value })}
                    placeholder="Banco"
                    placeholderTextColor={ticketColors.ink3}
                    editable={!isSaving}
                    accessibilityLabel="Banco del cheque"
                    style={[styles.checkoutCheckInput, webTextInputAutofillStyle(), { flex: 1 }]}
                  />
                </View>
                <TouchableOpacity
                  onPress={() => onAddSplitPayment('cheque')}
                  disabled={isSaving}
                  accessibilityRole="button"
                  accessibilityLabel="Agregar otro cheque"
                  style={styles.checkoutAddCheckButton}
                  testID="pos.checkout.add-check"
                >
                  <Ionicons name="add-circle-outline" size={15} color={ticketColors.gold} />
                  <Text style={styles.checkoutAddCheckButtonText}>Agregar otro cheque</Text>
                </TouchableOpacity>
              </View>
            ) : null}

            {paymentDrafts.length > 1 ? (
              <TouchableOpacity
                style={modalExperience.dangerButton}
                onPress={() => onRemoveDraft(draft.id)}
                disabled={isSaving}
                accessibilityRole="button"
                accessibilityLabel="Quitar pago"
                testID="pos.checkout.remove-draft"
              >
                <Text style={modalExperience.dangerButtonText}>Quitar pago</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        ))}
        <TouchableOpacity
          style={styles.checkoutAddPaymentButton}
          onPress={() => onAddSplitPayment()}
          disabled={isSaving}
          accessibilityRole="button"
          accessibilityLabel="Agregar otro medio de pago"
          testID="pos.checkout.add-split"
        >
                          <Ionicons name="add-circle-outline" size={18} color={ticketColors.gold} />
          <Text style={styles.checkoutAddPaymentButtonText}>Agregar otro medio de pago</Text>
        </TouchableOpacity>
      </View>

      {/* Selector de cliente a nivel venta — debajo de los medios de pago */}
      <CheckoutSaleCustomerSelector
        visible={visible}
        customers={customerEligibleCustomers}
        customersTotal={customersTotal}
        customerPageIndex={customerPageIndex}
        customerPageSize={customerPageSize}
        onLoadMoreCustomers={onLoadMoreCustomers}
        saleCustomerId={saleCustomerId}
        packageRequired={cartHasPackage}
        forceExpanded={fiadoNeedsCustomer}
        creditRequired={fiadoNeedsCustomer}
        onSelectCustomer={handleSelectSaleCustomer}
        onQuickAddCustomer={() => setQuickAddVisible(true)}
        disabled={isSaving}
        loyaltyEnabled={loyaltyEnabled}
        loyaltyBalance={loyaltyBalance}
        loyaltyProgram={loyaltyProgram}
        loyaltyRedeemPoints={loyaltyRedeemPoints}
        loyaltyRedeemDiscount={loyaltyRedeemDiscount}
        onLoyaltyRedeemChange={setLoyaltyRedeemPoints}
      />

      {secondaryFieldsCollapsed ? (
        <View>
          <TouchableOpacity
            style={styles.checkoutAddPaymentButton}
            onPress={() => setSecondaryOpen((v) => !v)}
            accessibilityRole="button"
            accessibilityLabel="Más opciones"
            accessibilityState={{ expanded: secondaryOpen }}
            testID="pos.checkout.more-options"
          >
            <Ionicons name="options-outline" size={15} color={ticketColors.ink2} />
            <Text style={styles.checkoutAddPaymentButtonText}>Más opciones</Text>
            <Ionicons
              name={secondaryOpen ? 'chevron-up' : 'chevron-down'}
              size={14}
              color={ticketColors.ink3}
            />
          </TouchableOpacity>
          {secondaryOpen ? secondaryFields : null}
        </View>
      ) : (
        secondaryFields
      )}
      <CheckoutQuickAddCustomerSheet
        visible={quickAddVisible}
        onClose={() => setQuickAddVisible(false)}
        onCreated={handleQuickAddCreated}
      />
    </View>
  );
}
